/**
 * AsyncProcessingStatus Component
 *
 * Shows document processing status with async job support.
 * Uses BullMQ job queue with polling for progress updates.
 * Includes cancel/retry functionality and queue position display.
 */

import { useEffect } from 'react';
import {
  CheckCircle,
  Circle,
  Loader2,
  AlertCircle,
  FileText,
  ScanText,
  Eye,
  CheckCheck,
  DollarSign,
  XCircle,
  RotateCcw,
  Clock,
  ListOrdered,
} from 'lucide-react';
import { useAsyncProcessing, AsyncProcessingState } from '../../hooks';
import type { DocumentProcessingStatus } from '../../types';

interface AsyncProcessingStatusProps {
  documentId: string;
  autoStart?: boolean;
  onComplete?: (status: DocumentProcessingStatus) => void;
  onCancel?: () => void;
}

type ProcessingStage = 'pending' | 'queued' | 'layer1' | 'layer2' | 'layer3' | 'completed' | 'failed';

const STAGES: Array<{
  key: ProcessingStage;
  label: string;
  icon: typeof FileText;
  layer?: number;
}> = [
  { key: 'pending', label: 'Uploaded', icon: FileText },
  { key: 'queued', label: 'Queued for Processing', icon: ListOrdered },
  { key: 'layer1', label: 'Text Extraction (Layer 1)', icon: ScanText, layer: 1 },
  { key: 'layer2', label: 'OCR Processing (Layer 2)', icon: ScanText, layer: 2 },
  { key: 'layer3', label: 'Vision Verification (Layer 3)', icon: Eye, layer: 3 },
  { key: 'completed', label: 'Completed', icon: CheckCheck },
];

function getStageIndex(state: AsyncProcessingState): number {
  const { state: jobState, currentLayer } = state;

  switch (jobState) {
    case 'idle':
    case 'starting':
      return 0;
    case 'queued':
      return 1;
    case 'processing':
    case 'fallback_sync':
      // Map current layer to stage index
      if (currentLayer === 1) return 2;
      if (currentLayer === 2) return 3;
      if (currentLayer === 3) return 4;
      return 2; // Default to layer 1 if unknown
    case 'completed':
      return 5;
    case 'failed':
    case 'cancelled':
      return -1;
    default:
      return 0;
  }
}

function StageIcon({
  stage,
  currentStageIndex,
  stageIndex,
  failed,
  cancelled,
  layerProgress,
}: {
  stage: typeof STAGES[number];
  currentStageIndex: number;
  stageIndex: number;
  failed: boolean;
  cancelled: boolean;
  layerProgress?: AsyncProcessingState['layerProgress'];
}) {
  const Icon = stage.icon;

  // Check layer-specific status
  if (layerProgress && stage.layer) {
    const layerKey = `layer${stage.layer}` as keyof typeof layerProgress;
    const layerStatus = layerProgress[layerKey];

    if (layerStatus.status === 'processing') {
      return <Loader2 size={20} className="text-primary-600 dark:text-primary-400 animate-spin" />;
    }
    if (layerStatus.status === 'complete') {
      return <CheckCircle size={20} className="text-green-500 dark:text-green-400" />;
    }
  }

  // Cancelled state
  if (cancelled && stageIndex >= currentStageIndex) {
    return <XCircle size={20} className="text-gray-400 dark:text-gray-500" />;
  }

  // Failed state
  if (failed && stageIndex >= currentStageIndex) {
    return <AlertCircle size={20} className="text-red-500 dark:text-red-400" />;
  }

  // Completed stage
  if (stageIndex < currentStageIndex) {
    return <CheckCircle size={20} className="text-green-500 dark:text-green-400" />;
  }

  // Current stage (in progress)
  if (stageIndex === currentStageIndex) {
    return <Loader2 size={20} className="text-primary-600 dark:text-primary-400 animate-spin" />;
  }

  // Future stage
  return <Circle size={20} className="text-gray-300 dark:text-gray-600" />;
}

export function AsyncProcessingStatus({
  documentId,
  autoStart = true,
  onComplete,
  onCancel,
}: AsyncProcessingStatusProps) {
  const {
    state,
    startProcessing,
    pollExistingJobs,
    cancelJob,
    retryJob,
    isProcessing,
    canCancel,
    canRetry,
  } = useAsyncProcessing({
    pollInterval: 2000,
    onComplete: (docId, final) => {
      // Prefer the fresh counts passed at completion; fall back to state only
      // when the hook could not supply them (the closure over `state` is stale).
      onComplete?.({
        id: docId,
        status: 'completed',
        page_count: final?.totalPages ?? state.totalPages,
        pages_processed: final?.pagesProcessed ?? state.pagesProcessed,
      });
    },
  });

  // Auto-start processing when component mounts (if autoStart is true)
  // Otherwise, poll for existing jobs (used when processing was already started)
  useEffect(() => {
    if (state.state === 'idle') {
      if (autoStart) {
        startProcessing(documentId);
      } else {
        // Poll for existing jobs since processing was started elsewhere
        pollExistingJobs(documentId);
      }
    }
  }, [autoStart, documentId, startProcessing, pollExistingJobs, state.state]);

  const currentStageIndex = getStageIndex(state);
  const failed = state.state === 'failed';
  const cancelled = state.state === 'cancelled';
  const completed = state.state === 'completed';

  // Format estimated time remaining
  const formatTimeRemaining = (seconds: number | null) => {
    if (!seconds || seconds <= 0) return null;
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  const timeRemaining = formatTimeRemaining(state.estimatedTimeRemaining);

  // Loading state
  if (state.state === 'idle') {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center">
          <Loader2 size={24} className="text-primary-600 dark:text-primary-400 animate-spin" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Preparing...</span>
        </div>
      </div>
    );
  }

  const handleCancel = async () => {
    await cancelJob();
    onCancel?.();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header with status and actions */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Processing Status</span>
            {state.state === 'queued' && state.queuePosition !== null && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                <ListOrdered size={12} />
                Position #{state.queuePosition}
              </span>
            )}
            {state.state === 'fallback_sync' && (
              <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded-full">
                Sync Mode
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {state.totalPages > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {state.pagesProcessed} / {state.totalPages} pages
              </span>
            )}
            {/* Cancel button */}
            {canCancel && (
              <button
                onClick={handleCancel}
                className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center gap-1 px-2 py-1 rounded border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <XCircle size={12} />
                Cancel
              </button>
            )}
            {/* Retry button */}
            {canRetry && (
              <button
                onClick={retryJob}
                className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 flex items-center gap-1 px-2 py-1 rounded border border-primary-200 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <RotateCcw size={12} />
                Retry
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main progress bar */}
      {!failed && !cancelled && (
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Overall Progress</span>
            <span>{state.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                completed ? 'bg-green-500' : 'bg-primary-600'
              }`}
              style={{ width: `${state.progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>{state.message}</span>
            {timeRemaining && !completed && (
              <span className="flex items-center gap-1">
                <Clock size={10} />
                Est. {timeRemaining} remaining
              </span>
            )}
          </div>
        </div>
      )}

      {/* Layer progress bars */}
      {isProcessing && state.currentLayer > 0 && !failed && !completed && (
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 space-y-2">
          {[1, 2, 3].map(layer => {
            const layerKey = `layer${layer}` as keyof typeof state.layerProgress;
            const layerStatus = state.layerProgress[layerKey];
            const layerNames = ['Text Extraction', 'OCR', 'Vision Verification'];

            return (
              <div key={layer} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 w-28">
                  Layer {layer}: {layerNames[layer - 1]}
                </span>
                <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      layerStatus.status === 'complete'
                        ? 'bg-green-500'
                        : layerStatus.status === 'processing'
                          ? 'bg-primary-500'
                          : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                    style={{ width: `${layerStatus.progress}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500 w-12 text-right">
                  {layerStatus.status === 'complete' ? (
                    <CheckCircle size={12} className="inline text-green-500 dark:text-green-400" />
                  ) : layerStatus.status === 'processing' ? (
                    `${layerStatus.progress}%`
                  ) : (
                    'Pending'
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Stages */}
      <div className="p-4">
        <div className="space-y-3">
          {STAGES.map((stage, idx) => {
            const isActive = idx === currentStageIndex && !failed && !cancelled && !completed;
            const isCompleted = idx < currentStageIndex || (completed && idx <= 5);
            const isFailed = failed && idx >= currentStageIndex;
            const isCancelled = cancelled && idx >= currentStageIndex;

            return (
              <div
                key={stage.key}
                className={`flex items-center gap-3 ${
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : isCompleted
                      ? 'text-green-600 dark:text-green-400'
                      : isFailed
                        ? 'text-red-500 dark:text-red-400'
                        : isCancelled
                          ? 'text-gray-400 dark:text-gray-500'
                          : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                <StageIcon
                  stage={stage}
                  currentStageIndex={currentStageIndex}
                  stageIndex={idx}
                  failed={failed}
                  cancelled={cancelled}
                  layerProgress={state.layerProgress}
                />
                <span
                  className={`text-sm ${
                    isActive ? 'font-medium' : isCompleted ? 'font-normal' : ''
                  }`}
                >
                  {stage.label}
                </span>
                {isActive && (
                  <span className="text-xs text-primary-500 dark:text-primary-400 ml-auto">In progress...</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Cost display */}
        {(state.costs.total > 0 || completed) && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <DollarSign size={16} />
              Processing Costs
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
              <div className="flex justify-between">
                <span>Extraction:</span>
                <span>${state.costs.extraction.toFixed(4)}</span>
              </div>
              <div className="flex justify-between">
                <span>OCR:</span>
                <span>${state.costs.ocr.toFixed(4)}</span>
              </div>
              <div className="flex justify-between">
                <span>Vision:</span>
                <span>${state.costs.vision.toFixed(4)}</span>
              </div>
              <div className="col-span-2 flex justify-between border-t border-gray-200 dark:border-gray-700 pt-1 font-medium">
                <span>Total:</span>
                <span>${state.costs.total.toFixed(4)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {failed && state.error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-start text-red-700 dark:text-red-300">
              <AlertCircle size={18} className="mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Processing Failed</p>
                <p className="text-sm mt-1">{state.error}</p>
                {canRetry && (
                  <button
                    onClick={retryJob}
                    className="mt-2 text-sm text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-200 font-medium flex items-center gap-1"
                  >
                    <RotateCcw size={14} />
                    Retry Processing
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cancelled message */}
        {cancelled && (
          <div className="mt-4 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div className="flex items-center text-gray-600 dark:text-gray-400">
              <XCircle size={18} className="mr-2" />
              <span className="text-sm">Processing was cancelled</span>
            </div>
          </div>
        )}

        {/* Completion message */}
        {completed && (
          <div className="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <div className="flex items-center text-green-700 dark:text-green-300">
              <CheckCircle size={18} className="mr-2" />
              <span className="text-sm font-medium">Document processed successfully</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AsyncProcessingStatus;
